import { useCallback, useEffect } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { toast } from 'sonner';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { RecetEditorSidebar } from '@/features/recet/components/editor/recet-editor-sidebar';
import { AdminSidebar } from './admin-sidebar';
import { AdminNavbar } from './admin-navbar';

const TECNOLOGIA_EDITOR_PATTERN = /^\/admin\/recet\/tecnologias\/\d+$/;
import { useIdleTimeout } from '@/shared/hooks/use-idle-timeout';
import { logoutApi } from '@/lib/api/client';
import { useOnboardingStatusQuery } from '@/features/onboarding/queries/onboarding.queries';
import { OnboardingWizard } from '@/features/onboarding/components/onboarding-wizard';
import { Skeleton } from '@/components/ui/skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { GuiaInteractivaProvider } from '@/features/guia-interactiva/components/guia-interactiva-provider';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutos

export function AdminLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: onboardingStatus, isLoading: onboardingLoading } = useOnboardingStatusQuery();
  const pathname = useRouterState({ select: s => s.location.pathname });
  const isTecnologiaEditor = TECNOLOGIA_EDITOR_PATTERN.test(pathname);

  const handleIdle = useCallback(async () => {
    await logoutApi();
    navigate({ to: '/login' });
    toast.info('Sesion cerrada por inactividad.');
  }, [navigate]);

  useIdleTimeout(handleIdle, IDLE_TIMEOUT_MS);

  useEffect(() => {
    const saved = localStorage.getItem('admin-theme');
    if (saved === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (saved === 'light') {
      document.documentElement.classList.remove('dark');
    }
    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, []);

  const handleOnboardingComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
  }, [queryClient]);

  // Show loading skeleton while checking onboarding status
  if (onboardingLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="space-y-4 text-center">
          <Skeleton className="mx-auto h-8 w-48" />
          <Skeleton className="mx-auto h-4 w-32" />
        </div>
      </div>
    );
  }

  // Show onboarding wizard if not completed
  if (onboardingStatus && !onboardingStatus.completado) {
    const isTutorial = onboardingStatus.esTutorial;
    return (
      <OnboardingWizard
        data={onboardingStatus}
        isTutorial={isTutorial}
        onComplete={handleOnboardingComplete}
      />
    );
  }

  return (
    <GuiaInteractivaProvider>
      <SidebarProvider>
        {isTecnologiaEditor ? <RecetEditorSidebar /> : <AdminSidebar />}
        <SidebarInset className="overflow-hidden">
          {!isTecnologiaEditor && <AdminNavbar />}
          <main
            className={
              isTecnologiaEditor
                ? 'bg-background flex-1 overflow-auto'
                : 'from-primary/15 via-background to-primary/20 flex-1 overflow-auto bg-linear-to-br p-2 md:p-3'
            }
          >
            <Outlet />
          </main>
        </SidebarInset>
      </SidebarProvider>
    </GuiaInteractivaProvider>
  );
}
